import React, { useCallback, useEffect, useState } from 'react';
import { ExternalAction, ExternalData } from '../../@types/external-api';
import {
  makeExternalCallErrorData,
  makeExternalCallSuccessData,
  makeExternalDataInitialData,
  makeExternalDataSuccessData,
} from '../../helpers/external-data';
import TranslationContext, { TranslationsContextType } from './translations-context';
import { Translation } from './translations-model';
import translationsService from './translations.service';

const TranslationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const locals = import.meta.env.VITE_APP_AVAILABLE_LANGUAGES?.split(',') || [];

  const [listTranslations, setListTranslations] = useState<ExternalData<Translation[]>>(makeExternalDataInitialData());
  const [addTranslationStatus, setAddTranslationStatus] = useState<ExternalAction>({});
  const [updateTranslationStatus, setUpdateTranslationStatus] = useState<ExternalAction>({});
  const [removeTranslationStatus, setRemoveTranslationsStatus] = useState<ExternalAction>({});
  const [listTranslationProperties, setListTranslationProperties] =
    useState<ExternalData<string[]>>(makeExternalDataInitialData());

  useEffect(() => {
    addTranslationStatus.success && setAddTranslationStatus({});
  }, [addTranslationStatus.success]);

  useEffect(() => {
    updateTranslationStatus.success && setUpdateTranslationStatus({});
  }, [updateTranslationStatus.success]);

  useEffect(() => {
    removeTranslationStatus.success && setRemoveTranslationsStatus({});
  }, [removeTranslationStatus.success]);

  const fetchListTranslations = useCallback(async (locales?: string[] | string, translateFrom?: string) => {
    try {
      setListTranslations(makeExternalDataInitialData());
      const data = await translationsService.listTranslations(locales, translateFrom);
      setListTranslations(makeExternalDataSuccessData(data));
    } catch (err: any) {
      setListTranslations(makeExternalCallErrorData(err));
    }
  }, []);

  const fetchListTranslationProperties = useCallback(async () => {
    try {
      setListTranslationProperties(makeExternalDataInitialData());
      const data = await translationsService.listTranslationsProperties();
      setListTranslationProperties(makeExternalDataSuccessData(data));
    } catch (err: any) {
      setListTranslationProperties(makeExternalCallErrorData(err));
    }
  }, []);

  const addTranslation: TranslationsContextType['addTranslation'] = useCallback(async translation => {
    try {
      setAddTranslationStatus(makeExternalDataInitialData());
      await translationsService.addTranslation(translation);
      setAddTranslationStatus(makeExternalCallSuccessData);
    } catch (err: any) {
      setAddTranslationStatus(makeExternalCallErrorData(err));
    }
  }, []);

  const updateTranslation: TranslationsContextType['updateTranslation'] = useCallback(async (id, updateTranslation) => {
    try {
      setUpdateTranslationStatus(makeExternalDataInitialData());
      await translationsService.updateTranslation(id, updateTranslation);
      setUpdateTranslationStatus(makeExternalCallSuccessData);
    } catch (err: any) {
      setUpdateTranslationStatus(makeExternalCallErrorData(err));
    }
  }, []);

  const removeTranslation: TranslationsContextType['removeTranslation'] = useCallback(async id => {
    try {
      setRemoveTranslationsStatus(makeExternalDataInitialData());
      await translationsService.removeTranslation(id);
      setRemoveTranslationsStatus(makeExternalCallSuccessData);
    } catch (err: any) {
      setRemoveTranslationsStatus(makeExternalCallErrorData(err));
    }
  }, []);

  return (
    <TranslationContext.Provider
      value={{
        listTranslations,
        listTranslationProperties,
        locals,
        addTranslationStatus,
        updateTranslationStatus,
        removeTranslationStatus,
        fetchListTranslations,
        fetchListTranslationProperties,
        addTranslation,
        updateTranslation,
        removeTranslation,
      }}>
      {children}
    </TranslationContext.Provider>
  );
};
export default TranslationProvider;
